/*
 * Platform Analytics Experience — per-platform profile registry.
 *
 * This module is the single definition layer for the redesigned individual
 * platform analytics pages (Cross-Platform Analytics design-system adoption,
 * ADDITIVE_ONLY_EXISTING_ANALYTICS_PRESERVED). It is deliberately isolated:
 * nothing here is imported by ConnectedPlatformAnalyticsPage,
 * ProgrammaticChannelAnalyticsPage, AnalyticsReportShell, or any excluded
 * page (Facebook, Instagram, Cinema), and it imports nothing from them.
 *
 * Governance invariants honored here:
 * - Metric vocabulary is restricted to fields the governed topline contract
 *   (canonical_reporting_v1 via getToplinePerformance) actually carries.
 *   Metrics a platform does not report are declared, never fabricated.
 * - Ratios are formatted from service-computed values — this layer never
 *   re-derives spend or efficiency from other fields for live data.
 * - Reported spend is client-billed (gross). Net media is server-gated
 *   (spendNetBasisVerified is false) and never rendered here.
 * - reportSourceLabel/candidateFields/genericScan are the governed mapping
 *   between an advertiser and its configured Looker Studio report. They are
 *   retained: the mapping still drives the Connected report status and stays
 *   available to internal operator tooling. What is gone is the CTA — the
 *   page no longer offers a viewer a way out to that external surface, so
 *   there is no reportButtonLabel to render.
 */

import {
  COST_PER_COMPLETED_VIEW_DEFINITION,
  COST_PER_COMPLETED_VIEW_LABEL,
  formatCostPerCompletedView,
} from "./costPerCompletedView";

/*
 * KPI profile kinds — how a platform's primary metric strip is CHOSEN.
 *
 * A platform's headline metrics follow from how the inventory is bought and
 * judged, not from which fields its payload happens to carry. Naming the kinds
 * here keeps that decision in the registry, where it can be stated once and
 * read by every surface, instead of becoming a `platform === "ctv"` branch in
 * each page that renders a KPI strip.
 *
 * Only video-completion-first is declared so far, because it is the only kind
 * that currently needs to override the generic click-oriented strip. Display,
 * Search and the other click/conversion channels declare nothing and keep the
 * default — which is the point: this mechanism can only ever ADD an explicit
 * exception, never silently restate what the untouched channels already do.
 */
export const VIDEO_COMPLETION_FIRST_KPI_PROFILE = "video_completion_first";

/*
 * TrueView-delivery inventory: bought and reconciled on Google's direct
 * TrueView count, with implied 100% completions derived by the canonical
 * sheet ingest. Declaring the kind opts every focused surface into YouTube's
 * own ordered commandMetricKeys instead of the generic mixed-flight strip.
 */
export const TRUEVIEW_DELIVERY_KPI_PROFILE = "trueview_delivery";

/*
 * Click-and-outcome-first inventory: bought on the click, judged on what the
 * click cost and what it produced.
 *
 * Declared for the same reason the video kind was. The generic cross-platform
 * strip is a MIXED-FLIGHT vocabulary — it has to hold a campaign running CTV,
 * YouTube and Display at once, so two of its eight positions go to the
 * campaign's video story (Cost Per Qualified Video View, Qualified Video
 * Views). Focused on Display those two positions describe inventory that
 * serves no video at all: Display's `videoStarts` is a reported 0 and it has
 * no qualified-video signal, so both cards rendered a permanent `—` while CPC
 * and CPM — the two figures Display is actually bought and judged on — had
 * nowhere to appear.
 *
 * That is the CTV defect pointed the other way, and it is fixed the same way:
 * by declaring the ruling in the registry once, so the focused strip and the
 * dedicated Display page are the same eight cards by construction rather than
 * by two lists someone has to keep in agreement.
 */
export const CLICK_OUTCOME_FIRST_KPI_PROFILE = "click_outcome_first";

const currencyPreciseFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/*
 * An axis tick. Whole dollars when the tick lands on one, cents only when the
 * scale is fine enough to need them — a tick is a scale mark, not a reported
 * total, so "$5,000" reads better than "$5,000.00" while "$7.50" keeps what
 * the scale actually says.
 */
const currencyWholeFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const formatCurrencyAxisTick = (value) =>
  Number.isInteger(value)
    ? currencyWholeFormatter.format(value)
    : currencyPreciseFormatter.format(value);

export function isFiniteMetricNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Half-up rounding to `places` decimals, on the DECIMAL value.
 *
 * `Number.prototype.toFixed` is not this. It rounds the binary double actually
 * stored, and a decimal literal frequently is not exactly representable — so
 * it rounds DOWN whenever the stored double lands a hair below the .5
 * boundary. `(0.045).toFixed(2)` is "0.04", `(0.145).toFixed(2)` is "0.14",
 * `(1.005).toFixed(2)` is "1.00", `(2.675).toFixed(2)` is "2.67": every one a
 * half-down answer to a half-up question.
 *
 * It matters most for a CTR, where the interesting digits are the last ones —
 * a display rate rounds at the second decimal, so a boundary case decides
 * whether the strip reads 0.05% or 0.06%. The exponential round trip re-reads
 * the number at the rounding position in DECIMAL, so the tie is judged on the
 * number as written rather than on bits the reader cannot see.
 *
 * Presentation only. Nothing here touches a numerator or a denominator: the
 * governed engines compute from exact integer counts and this rounds once, at
 * the very end, for display.
 */
export function roundHalfUp(value, places = 0) {
  if (!Number.isFinite(value)) return value;
  const sign = value < 0 ? -1 : 1;
  const magnitude = Math.abs(value);
  const shifted = Number(`${magnitude}e${places}`);
  if (!Number.isFinite(shifted)) return value;
  const result = Number(`${Math.round(shifted)}e-${places}`);
  return Number.isFinite(result) ? sign * result : value;
}

/**
 * A reported figure, in full.
 *
 * `options.compact` is accepted and IGNORED. Every count and every monetary
 * total on the analytics surfaces used to compact above a threshold — 28.1M
 * impressions, $87.1K spend — and the reader had to hover, or find the help
 * text, to learn the number that was actually reported. Compaction is a
 * display shortcut, not a fact about the data, so it is gone: the card face
 * now states the same figure the API returned, with thousands separators.
 * The option survives on the signature because the shared chart renderer and
 * the strips still pass it, and a formatter that threw on a hint it no longer
 * uses would be a worse contract than one that shrugs.
 *
 * `options.axis` marks a chart axis tick, where currency drops cents it does
 * not need — see `formatCurrencyAxisTick`. Cards never pass it.
 */
export function formatPlatformMetricValue(unit, value, options = {}) {
  if (!isFiniteMetricNumber(value)) return "—";
  switch (unit) {
    /*
     * Money is stated to the cent, always. The old non-compact branch rounded
     * anything from $100 up to whole dollars and the compact branch went
     * further — Display's real client-billed CPC of $0.6076 once rendered "$1"
     * and its CPM of $0.3325 rendered "$0", a stated cost of nothing on a card
     * whose whole job is to say what the delivery cost. The currency's own
     * precision is the only rounding a reported total gets.
     */
    case "currency":
      return options.axis
        ? formatCurrencyAxisTick(value)
        : currencyPreciseFormatter.format(value);
    /*
     * A cost per single low-priced event (CPCV). Delegated to the CPCV
     * module's own formatter rather than reimplemented, so the dedicated
     * platform card and the Platform Analytics KPI strip round identically —
     * two roundings of one metric is exactly the parity drift the byte-pinned
     * twins exist to prevent.
     */
    case "unit_cost":
      return formatCostPerCompletedView(value);
    /*
     * An age in minutes, said the way every other freshness stamp on these
     * surfaces says it. A source that declared no timestamp reaches here as a
     * non-finite value and is caught by the guard above, so it renders the
     * canonical `—` rather than an age inferred from the request clock.
     */
    case "freshness":
      if (value < 60) return `${Math.round(value)}m ago`;
      if (value < 48 * 60) return `${Math.round(value / 60)}h ago`;
      return `${Math.round(value / (24 * 60))}d ago`;
    /* A whole count, every digit, thousands-separated. */
    case "count":
      return integerFormatter.format(value);
    /* Half-up on the decimal value — see `roundHalfUp`. `toFixed` alone
       decided these boundaries on the binary double, so a rate sitting exactly
       on half a hundredth could round either way. */
    case "percent_ratio_fixed":
      return `${roundHalfUp(value * 100, 2).toFixed(2)}%`;
    case "percent_ratio": {
      const percentValue = value * 100;
      const places = Math.abs(percentValue) < 10 ? 2 : 1;
      return `${roundHalfUp(percentValue, places).toFixed(places)}%`;
    }
    case "percent": {
      const places = Math.abs(value) < 10 ? 2 : 1;
      return `${roundHalfUp(value, places).toFixed(places)}%`;
    }
    /*
     * GA4 reports durations in seconds. Rendered as m/s rather than a raw
     * float, which reads as a count next to the other cards.
     */
    case "duration": {
      const totalSeconds = Math.max(0, Math.round(value));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
    }
    default:
      return String(value);
  }
}

/*
 * Metric registry. `raw` names the field on the governed topline payload
 * (`topline.raw`), which the analytics topline service computes canonically.
 * `kind: "volume"` metrics may drive the delivery chart; `kind: "efficiency"`
 * metrics render as cards and the paired chart line, but only where the
 * service reported them.
 */
export const PLATFORM_ANALYTICS_METRICS = [
  { key: "spend", label: "Spend", unit: "currency", kind: "volume", raw: "spend", definition: "Client-billed media spend for the analysis window." },
  { key: "impressions", label: "Impressions", unit: "count", kind: "volume", raw: "impressions" },
  { key: "clicks", label: "Clicks", unit: "count", kind: "volume", raw: "clicks" },
  { key: "videoViews", label: "Video Views", unit: "count", kind: "volume", raw: "videoViews" },
  { key: "videoStarts", label: "Video Starts", unit: "count", kind: "volume", raw: "videoStarts" },
  { key: "videoCompletions", label: "Completed Views", unit: "count", kind: "volume", raw: "videoCompletions" },
  { key: "trueViewViews", label: "TRUEVIEW VIEWS", unit: "count", kind: "volume", raw: "trueViewViews", definition: "TrueView views read directly from the governed Google report." },
  { key: "videoPlayed25", label: "25% Plays", unit: "count", kind: "volume", raw: "videoPlayed25" },
  { key: "videoPlayed50", label: "50% Plays", unit: "count", kind: "volume", raw: "videoPlayed50" },
  { key: "videoPlayed75", label: "75% Plays", unit: "count", kind: "volume", raw: "videoPlayed75" },
  ...[25, 50, 75, 100].map(milestone => ({
    key: `youtubePlayed${milestone}Rate`, label: `Video played to ${milestone}%`,
    unit: "percent_ratio_fixed", kind: "efficiency", raw: `youtubePlayed${milestone}Rate`,
    definition: `Percentage of video player initiations that reached ${milestone}% of the video.`,
  })),
  { key: "youtubeVideoCompletions", label: "100% VIDEO COMPLETIONS", unit: "count", kind: "volume", raw: "videoCompletions", compact: false, definition: "Sum of each source row's impressions multiplied by its normalized Video played to 100% rate." },
  { key: "engagements", label: "Engagements", unit: "count", kind: "volume", raw: "engagements" },
  { key: "conversions", label: "Conversions", unit: "count", kind: "volume", raw: "conversions", definition: "Conversions attributed and reported by the delivering ad platform, including view-through it credits; the platform's configured attribution windows apply." },
  /*
   * The governed OUTCOME slot.
   *
   * Same underlying field as `conversions` above, and deliberately a separate
   * key rather than a second spelling of it. `conversions` is the raw metric
   * under its generic name; this is the slot whose NAME is a backend ruling —
   * "Ad-Attributed Site Visits" only where the live tracker configuration
   * proved every counted event is a site-visit pixel fire, and the generic
   * "Conversions" everywhere else, including every uncertainty.
   *
   * The label and definition here are the fail-closed defaults. A strip that
   * holds the campaign's `outcomeSemantics` overrides both through
   * `resolveOutcomeConversionsPresentation`; a surface that holds no
   * declaration renders exactly what is written here, which is always
   * truthful. The specific name is an earned claim and is never the default.
   */
  { key: "outcome", label: "Conversions", unit: "count", kind: "volume", raw: "conversions", definition: null },
  { key: "sessions", label: "GA Sessions", unit: "count", kind: "volume", raw: "sessions" },
  /*
   * GA4 measurement metrics. These describe site activity, not media delivery,
   * and exist so a measurement page can use the shared KPI strip without
   * paid-media concepts being forced onto it. A page that reports spend never
   * declares these, and a measurement page never declares spend.
   */
  { key: "ga4Users", label: "Users", unit: "count", kind: "volume", raw: "ga4Users", definition: "Distinct users GA4 recorded for the property in the analysis window." },
  { key: "ga4NewUsers", label: "New Users", unit: "count", kind: "volume", raw: "ga4NewUsers", definition: "Users whose first session with the property fell in the analysis window." },
  { key: "ga4Sessions", label: "Sessions", unit: "count", kind: "volume", raw: "ga4Sessions", definition: "Sessions GA4 recorded for the property in the analysis window." },
  { key: "ga4EngagedSessions", label: "Engaged Sessions", unit: "count", kind: "volume", raw: "ga4EngagedSessions", definition: "Sessions GA4 classified as engaged." },
  { key: "ga4EngagementRate", label: "Engagement Rate", unit: "percent", kind: "efficiency", raw: "ga4EngagementRate", definition: "Engaged sessions divided by sessions, as reported by GA4." },
  { key: "ga4EventCount", label: "Event Count", unit: "count", kind: "volume", raw: "ga4EventCount", definition: "Events GA4 recorded for the property in the analysis window." },
  { key: "ga4AvgSessionDuration", label: "Avg Session Duration", unit: "duration", kind: "efficiency", raw: "ga4AvgSessionDuration", definition: "Average session duration as reported by GA4." },
  { key: "ctr", label: "CTR", unit: "percent", kind: "efficiency", raw: "ctr", definition: "Clicks divided by impressions, computed by the governed reporting service." },
  { key: "cpm", label: "CPM", unit: "currency", kind: "efficiency", raw: "cpm", definition: "Client-billed spend per 1,000 impressions." },
  { key: "cpc", label: "CPC", unit: "currency", kind: "efficiency", raw: "cpc", definition: "Client-billed spend per click." },
  { key: "cpa", label: "CPA", unit: "currency", kind: "efficiency", raw: "cpa", definition: "Client-billed spend per conversion." },
  { key: "vcr", label: "Completion Rate", unit: "percent", kind: "efficiency", raw: "vcr", definition: "Completed views divided by video starts, as reported by the platform." },
  { key: "vvr", label: "Video View Rate", unit: "percent", kind: "efficiency", raw: "vvr", definition: "Total TrueView views divided by total impressions." },
  { key: "cpqv", label: "CPQV", unit: "unit_cost", kind: "efficiency", raw: "cpqv", definition: "Governed gross client-billed YouTube spend divided by TrueView / qualified video views." },
  { key: "cpcv", label: COST_PER_COMPLETED_VIEW_LABEL, unit: "unit_cost", kind: "efficiency", raw: "cpcv", definition: COST_PER_COMPLETED_VIEW_DEFINITION },
  /*
   * How old the reporting behind every other card is. `kind: "governance"`
   * rather than volume or efficiency, and deliberately so: it is not a
   * reading of the media, it is a statement about the evidence. That keeps it
   * out of `selectable` (a freshness stamp is not a chart series) without a
   * card-by-card exception, and `raw` is null because the age is resolved
   * from the resolved canonical freshness timestamp, not from the metric bag.
   */
  { key: "dataFreshness", label: "Data Freshness", unit: "freshness", kind: "governance", raw: null, definition: null },
  { key: "revenue", label: "Revenue", unit: "currency", kind: "volume", raw: "revenue", definition: "Commerce revenue as reported by the connected source — not media spend." },
  { key: "orders", label: "Orders", unit: "count", kind: "volume", raw: "orders" },
  { key: "aov", label: "AOV", unit: "currency", kind: "efficiency", raw: "aov", definition: "Average order value as reported by the connected source." },
  { key: "customers", label: "Customers", unit: "count", kind: "volume", raw: "customers" },
  { key: "leads", label: "Leads", unit: "count", kind: "volume", raw: "leads", definition: "Lead submissions as reported by the platform." },
];

const METRIC_BY_KEY = new Map(PLATFORM_ANALYTICS_METRICS.map((metric) => [metric.key, metric]));

export function resolvePlatformMetric(key) {
  return METRIC_BY_KEY.get(String(key || "")) || null;
}

/*
 * Page profiles. `platformKey` matches the analytics topline / optimus
 * snapshot channel key. `accent` follows the platform identity color already
 * validated for the Cross-Platform Analytics categorical set — identity,
 * never rank. `commandMetricKeys` is the 6–8 card strip (first entry is the
 * default chart metric); `chartMetricKeys` are the volume metrics offered on
 * the delivery chart; `pairedEfficiencyKey` is the default efficiency line
 * for the fixture design preview.
 *
 * Naming — three fields, three jobs, and they are not interchangeable:
 * - `heroLabel` is the SEMANTIC platform name. It is what the page says out
 *   loud: chart series legends, the Creative Performance subtitle, the
 *   Audience Synthesis line, the spoken delivery narrative. It has to read
 *   correctly mid-sentence, so it carries no punctuation or styling.
 * - `heroDisplayLabel` is the OPTIONAL WORDMARK — the large word in the hero
 *   capsule, read by the hero and nowhere else. Declare it only when the
 *   wordmark is not the whole semantic name: the two Google surfaces show
 *   "Google" and let the capsule name which Google product this is, and
 *   ChatGPT Search shows "ChatGPT" the same way. The hero falls back to
 *   `heroLabel`, so every other profile needs nothing.
 * - `heroTag` is the small nested capsule to the wordmark's right. Usually the
 *   trailing word "Analytics"; on the Google and ChatGPT Search pages it is the
 *   product itself ("Search", "Analytics"), which is what makes
 *   GOOGLE + [SEARCH] read as one thing rather than a name with a version
 *   stapled beside it.
 *
 * The split exists because these were once ONE field, and the hero and the
 * page's prose wanted different words from it. Writing the wordmark into
 * `heroLabel` put it mid-sentence — "Governed Audience Intelligence for
 * Google" — while the sentences need the full name. A wordmark and a noun are
 * different things and a page needs both.
 *
 * Audience Data Synthesis binding:
 * - `audienceSupport: "platform_specific"` — the backend audience registry
 *   (SUPPORTED_PLATFORMS in services/audienceIntelligenceService.js) carries
 *   this platform, and `audiencePlatformKey` is the exact key requested.
 * - `audienceSupport: "shared_only"` — the backend contract has no key for
 *   this platform, so the page requests the governed shared scope ("overall")
 *   and every render is labeled as shared. Platform-specific synthesis for
 *   these platforms requires a governed backend registry extension first
 *   (BACKEND_CONTRACT_REQUIRED) — it is never faked from the shared record.
 */
export const PLATFORM_ANALYTICS_PROFILES = {
  /*
   * Programmatic channels carry two things the social profiles do not.
   *
   * `creativePerformancePlatformKey` opts a page into the Creative Performance
   * section. Only platforms whose connector actually reports creative-level
   * delivery declare it — today that is the StackAdapt-trafficked programmatic
   * channels. Every other page renders the governed unavailable state instead,
   * which is a statement about the source rather than about the creatives.
   *
   * `distributionTransparencyChannel` preserves the inventory-transparency
   * module these channels already had before they adopted this shell. A shared
   * design does not mean a shared metric set, and a working analytics
   * capability is not dropped to complete a visual migration.
   */
  ctv: {
    platformKey: "ctv",
    audienceSupport: "shared_only",
    routeKey: "/ctv-analytics",
    pageTitle: "CTV Analytics",
    heroLabel: "CTV",
    heroTag: "Analytics",
    accent: "#1da36f",
    /*
     * CTV is modelled as VIDEO-COMPLETION-FIRST inventory, and this key set is
     * that ruling written down once.
     *
     * The eight cards read as one argument: what did we invest, how much did
     * we deliver, how many plays STARTED, how many FINISHED, what share
     * finished, what did the delivery cost, what did a finished play cost, and
     * how current is all of it.
     *
     * Neither clicks nor CTR appear. The CTR card went first — CTV's click
     * field is a zero-filled placeholder and the governed efficiency contract
     * refuses to read CTV on a click rate anywhere else. The clicks COUNT
     * stayed for a while on the reasoning that a count is a fact where the
     * rate was a misleading statement. That was true and still insufficient:
     * a primary KPI position is not neutral shelf space, it is an assertion
     * that the figure is one of the eight things this buy is judged on, and a
     * constant zero asserted the opposite of what CTV measures. The count is
     * not deleted — it remains in advanced analytics, exports and raw
     * reporting, where a fact with no headline claim attached belongs.
     */
    commandMetricKeys: ["spend", "impressions", "videoStarts", "videoCompletions", "vcr", "cpqv", "cpcv", "dataFreshness"],
    /*
     * The same ruling, reachable from the CROSS-platform surface. When
     * Platform Analytics is focused on this platform its KPI strip resolves
     * through here to the key set above rather than inheriting the generic
     * click-oriented strip — so the focused view and the dedicated page state
     * the same eight metrics, in the same order, by construction rather than
     * by two lists someone has to keep in agreement.
     */
    focusedKpiProfile: VIDEO_COMPLETION_FIRST_KPI_PROFILE,
    chartMetricKeys: ["spend", "impressions", "videoStarts", "videoCompletions"],
    pairedEfficiencyKey: "vcr",
    creativePerformancePlatformKey: "ctv",
    distributionTransparencyChannel: "ctv",
    reportSourceLabel: "CTV Report",
    candidateFields: ["ctvLink", "ctv", "ctvURL", "ctv_report"],
    genericScan: true,
    genericChannelKey: "ctv",
    pageCapabilities: ["CTV performance", "programmatic reporting", "delivery and creative review"],
    edenExamples: ['Try "show completed views" or "which creative delivered most?"'],
    edenPlaceholder: 'Ask EDEN — "show CTV completion rate"',
  },
  display: {
    platformKey: "display",
    audienceSupport: "shared_only",
    routeKey: "/display-analytics",
    pageTitle: "Display Analytics",
    heroLabel: "Display",
    heroTag: "Analytics",
    accent: "#4f7df0",
    /*
     * Display is CLICK-AND-OUTCOME-FIRST inventory, and this key set is that
     * ruling written down once.
     *
     * The eight cards read as one argument: what did we invest, how much did
     * we deliver, how many people responded, at what rate, what did a response
     * cost, what did the delivery cost, what did it produce, and how current is
     * all of it.
     *
     * No video metric appears, and that is the point. Display serves no video
     * — its `videoStarts` is a reported 0 and it carries no qualified-video
     * signal — so CPQV and Video Views previously occupied two of the eight
     * primary positions with a permanent `—`. A primary KPI position is not
     * neutral shelf space; it asserts that the figure is one of the eight
     * things this buy is judged on, and a permanently unavailable video card
     * asserted that of inventory that has no video to report. Video is not
     * deleted anywhere it is real: YouTube, CTV, TikTok and every all-platform
     * view keep their video cards untouched.
     *
     * CPC before CPM, which reverses the order this list carried before. The
     * strip reads as an argument rather than an inventory, and the cost of the
     * RESPONSE belongs beside the response rate that precedes it; the cost of
     * the DELIVERY then closes the cost pair. Both are stated by the backend
     * from client-billed spend — see the strip builder, which divides nothing.
     */
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpc", "cpm", "outcome", "dataFreshness"],
    /*
     * The same ruling, reachable from the CROSS-platform surface — exactly as
     * CTV declares above. Focusing Platform Analytics on Display resolves its
     * KPI strip through here instead of inheriting the mixed-flight strip, so
     * the focused view and the dedicated page state the same eight metrics in
     * the same order by construction.
     */
    focusedKpiProfile: CLICK_OUTCOME_FIRST_KPI_PROFILE,
    chartMetricKeys: ["spend", "impressions", "clicks", "conversions"],
    pairedEfficiencyKey: "ctr",
    creativePerformancePlatformKey: "display",
    distributionTransparencyChannel: "display",
    reportSourceLabel: "Display Report",
    candidateFields: ["displayLink", "display", "displayURL", "display_report"],
    genericScan: true,
    genericChannelKey: "display",
    pageCapabilities: [
      "display performance",
      "programmatic reporting",
      "delivery, efficiency, and creative review",
    ],
    edenExamples: ['Try "show CTR" or "which creative had the highest CTR?"'],
    edenPlaceholder: 'Ask EDEN — "show Display CTR"',
  },
  /*
   * Meta. The Facebook and Instagram pages carry reporting semantics that
   * matter financially, so they migrate as a container change only: the live
   * Meta ads library moves with them, and it is that component which owns the
   * publisher identity, the separately-governed Audience Network / Messenger
   * delivery, and the reconciliation that stops those placements inflating
   * Facebook totals. `metaAdsLibrary` is the binding that carries it.
   *
   * No connector reports Meta creative-level delivery through a routed
   * endpoint, so neither declares creativePerformancePlatformKey and both
   * render the governed platform_unsupported state — the same as every other
   * platform without a certified contract.
   */
  facebook: {
    platformKey: "facebook",
    audiencePlatformKey: "facebook",
    audienceSupport: "platform_specific",
    routeKey: "/facebook-analytics",
    pageTitle: "Facebook Analytics",
    heroLabel: "Facebook",
    heroTag: "Analytics",
    accent: "#7c6cf0",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpm", "cpc", "engagements", "videoViews"],
    chartMetricKeys: ["spend", "impressions", "clicks", "engagements", "videoViews"],
    pairedEfficiencyKey: "ctr",
    metaAdsLibrary: true,
    reportSourceLabel: "Facebook Report",
    candidateFields: ["facebookLink", "facebook", "facebookURL", "facebook_report"],
    genericScan: true,
    genericChannelKey: "facebook",
    pageCapabilities: ["Facebook performance", "paid social analysis", "creative and delivery review"],
    edenExamples: ['Try "show engagements" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show Facebook engagement"',
  },
  instagram: {
    platformKey: "instagram",
    audiencePlatformKey: "instagram",
    audienceSupport: "platform_specific",
    routeKey: "/instagram-analytics",
    pageTitle: "Instagram Analytics",
    heroLabel: "Instagram",
    heroTag: "Analytics",
    accent: "#d8409f",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpm", "cpc", "engagements", "videoViews"],
    chartMetricKeys: ["spend", "impressions", "clicks", "engagements", "videoViews"],
    pairedEfficiencyKey: "ctr",
    metaAdsLibrary: true,
    reportSourceLabel: "Instagram Report",
    candidateFields: ["instagramLink", "instagram", "instagramURL", "instagram_report"],
    genericScan: true,
    genericChannelKey: "instagram",
    pageCapabilities: ["Instagram performance", "paid social analysis", "content and engagement review"],
    edenExamples: ['Try "show engagements" or "show video views"'],
    edenPlaceholder: 'Ask EDEN — "show Instagram engagement"',
  },
  /*
   * The remaining programmatic channels. StackAdapt does not report
   * creative-level delivery for any of them, so none declares
   * creativePerformancePlatformKey and each renders the governed
   * platform_unsupported state — a statement about the source, not the
   * creatives. Inventory transparency is preserved only where
   * ProgrammaticChannelAnalyticsPage actually offered it.
   */
  video: {
    platformKey: "video",
    audienceSupport: "shared_only",
    routeKey: "/video-analytics",
    pageTitle: "Video Analytics",
    heroLabel: "Video",
    heroTag: "Analytics",
    accent: "#4f7df0",
    commandMetricKeys: ["spend", "impressions", "videoStarts", "videoCompletions", "vcr", "cpm", "clicks", "ctr"],
    chartMetricKeys: ["spend", "impressions", "videoStarts", "videoCompletions"],
    pairedEfficiencyKey: "vcr",
    distributionTransparencyChannel: "video",
    reportSourceLabel: "Video Report",
    candidateFields: ["videoLink", "video", "videoURL", "video_report"],
    genericScan: true,
    genericChannelKey: "video",
    pageCapabilities: ["video performance", "programmatic reporting", "delivery and completion review"],
    edenExamples: ['Try "show completed views" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show video completion rate"',
  },
  audio: {
    platformKey: "audio",
    audienceSupport: "shared_only",
    routeKey: "/audio-analytics",
    pageTitle: "Audio Analytics",
    heroLabel: "Audio",
    heroTag: "Analytics",
    accent: "#38a1b8",
    commandMetricKeys: ["spend", "impressions", "videoStarts", "videoCompletions", "vcr", "cpm", "clicks", "ctr"],
    chartMetricKeys: ["spend", "impressions", "videoStarts", "videoCompletions"],
    pairedEfficiencyKey: "vcr",
    distributionTransparencyChannel: "audio",
    reportSourceLabel: "Audio Report",
    candidateFields: ["audioLink", "audio", "audioURL", "audio_report"],
    genericScan: true,
    genericChannelKey: "audio",
    pageCapabilities: ["audio performance", "programmatic reporting", "delivery and completion review"],
    edenExamples: ['Try "show completed listens" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show audio completion rate"',
  },
  mobile: {
    platformKey: "mobile",
    audienceSupport: "shared_only",
    routeKey: "/mobile-analytics",
    pageTitle: "Mobile Analytics",
    heroLabel: "Mobile",
    heroTag: "Analytics",
    accent: "#7d64d9",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpm", "cpc", "conversions", "cpa"],
    chartMetricKeys: ["spend", "impressions", "clicks", "conversions"],
    pairedEfficiencyKey: "ctr",
    distributionTransparencyChannel: "mobile",
    reportSourceLabel: "Mobile Report",
    candidateFields: ["mobileLink", "mobile", "mobileURL", "mobile_report"],
    genericScan: true,
    genericChannelKey: "mobile",
    pageCapabilities: ["mobile performance", "programmatic reporting", "delivery and efficiency review"],
    edenExamples: ['Try "show CTR" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show mobile CTR"',
  },
  dooh: {
    platformKey: "dooh",
    audienceSupport: "shared_only",
    routeKey: "/dooh-analytics",
    pageTitle: "DOOH Analytics",
    heroLabel: "DOOH",
    heroTag: "Analytics",
    accent: "#b5820f",
    /*
     * DOOH is not a click medium. Offering CTR here would invite a comparison
     * the format cannot support, so the strip stays on delivery and cost.
     */
    commandMetricKeys: ["spend", "impressions", "cpm"],
    chartMetricKeys: ["spend", "impressions"],
    pairedEfficiencyKey: "cpm",
    distributionTransparencyChannel: "dooh",
    reportSourceLabel: "DOOH Report",
    candidateFields: ["doohLink", "dooh", "doohURL", "dooh_report"],
    genericScan: true,
    genericChannelKey: "dooh",
    pageCapabilities: ["DOOH performance", "programmatic reporting", "delivery and reach review"],
    edenExamples: ['Try "show impressions" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show DOOH impressions"',
  },
  ingame: {
    platformKey: "ingame",
    audienceSupport: "shared_only",
    routeKey: "/ingame-analytics",
    pageTitle: "In-Game Analytics",
    heroLabel: "In-Game",
    heroTag: "Analytics",
    accent: "#74b043",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpm"],
    chartMetricKeys: ["spend", "impressions", "clicks"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "In-Game Report",
    candidateFields: ["ingameLink", "ingame", "ingameURL", "ingame_report", "inGame", "inGameLink"],
    genericScan: true,
    genericChannelKey: "ingame",
    pageCapabilities: ["in-game performance", "programmatic reporting", "delivery and efficiency review"],
    edenExamples: ['Try "show impressions" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show in-game delivery"',
  },
  email: {
    platformKey: "email",
    audienceSupport: "shared_only",
    routeKey: "/email-analytics",
    pageTitle: "Email Analytics",
    heroLabel: "Email",
    heroTag: "Analytics",
    accent: "#3286c9",
    /*
     * Email reports sends and opens rather than impressions in the paid-media
     * sense, and the governed topline carries only the shared vocabulary, so
     * the strip stays on what the contract actually reports.
     */
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "conversions"],
    chartMetricKeys: ["spend", "impressions", "clicks", "conversions"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "Email Report",
    candidateFields: ["emailLink", "email", "emailURL", "email_report"],
    genericScan: true,
    genericChannelKey: "email",
    pageCapabilities: ["email performance", "delivery and engagement review", "campaign reporting"],
    edenExamples: ['Try "show clicks" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show email clicks"',
  },
  /*
   * Meta is the governed parent of the Meta publisher platforms, not a synonym
   * for Facebook. Its totals are assembled in analyticsToplineService by fanning
   * out across META_PARENT_REPORTING_CHANNELS (facebook, instagram,
   * audience_network, messenger) and summing them, because the snapshot
   * endpoint canonicalizes a literal "meta" channel to "facebook" — a Meta page
   * that requested its own key would report Facebook-only delivery under a Meta
   * label. Facebook and Instagram keep their own routes and their own narrower
   * scopes; this page never replaces them.
   *
   * No creativePerformancePlatformKey: metaCreativeAssetDeliveryService exists
   * but no route mounts it, so there is no client-safe creative-level Meta
   * contract to read. The page renders the governed unavailable state, which is
   * a statement about the source rather than about the creatives.
   */
  /*
   * GA4 is a MEASUREMENT source, not a media channel. `metricsSource: "ga4"`
   * routes the page at the one seam that matters — where the governed payload
   * comes from — so everything downstream (view model, KPI strip, window
   * controls, workspace) stays exactly the shared composition.
   *
   * It deliberately declares no spend metric and no spend basis: GA4 does not
   * report media cost, and a Net/Gross control here would imply it does.
   *
   * Distinct from `google_search`, which is PAID Google Search delivery on
   * /google-analytics. The two are different reporting products and the route
   * names have historically conflated them.
   */
  ga4: {
    platformKey: "ga4",
    /*
     * `sourceType` governs SEMANTICS — which controls and vocabulary the page
     * is entitled to. `metricsSource` names the ADAPTER that fills the payload.
     * Profiles that declare neither default to "paid_media" and the topline
     * service, which is why every existing page is untouched by this seam.
     */
    sourceType: "measurement",
    metricsSource: "ga4",
    audiencePlatformKey: "overall",
    audienceSupport: "shared_only",
    routeKey: "/ga4-analytics",
    pageTitle: "Google Analytics",
    /*
     * The hero is GOOGLE + [ANALYTICS]: the wordmark names the company, the
     * capsule names which Google product this page reports. It used to be
     * "Google Analytics" with a "GA4" capsule — the wordmark already ended in
     * "Analytics", so the capsule had nothing left to say and named the
     * product VERSION instead, telling a reader "GA4" before telling them
     * anything about the report.
     *
     * `heroLabel` stays the SEMANTIC name because it is what the page says out
     * loud — chart legends, the Audience Synthesis line, the delivery
     * narrative. "Governed Audience Intelligence for Google" would be true of
     * the wrong thing; the sentences need the whole product name.
     *
     * Internally the source stays `ga4`: `platformKey`, `metricsSource` and
     * the metric keys name the real integration and are untouched by a
     * presentation change.
     */
    heroLabel: "Google Analytics",
    heroDisplayLabel: "Google",
    heroTag: "Analytics",
    accent: "#e8a13a",
    commandMetricKeys: [
      "ga4Users",
      "ga4NewUsers",
      "ga4Sessions",
      "ga4EngagedSessions",
      "ga4EngagementRate",
      "ga4EventCount",
      "ga4AvgSessionDuration",
    ],
    chartMetricKeys: ["ga4Sessions", "ga4Users", "ga4NewUsers", "ga4EngagedSessions"],
    pairedEfficiencyKey: "ga4EngagementRate",
    reportSourceLabel: "Google Analytics Report",
    candidateFields: [
      "googleAnalyticsLink",
      "googleAnalytics",
      "google_analytics",
      "googleAnalyticsURL",
      "google_analytics_report",
    ],
    genericScan: true,
    genericChannelKey: "google_analytics",
    pageCapabilities: [
      "site performance",
      "audience and device engagement",
      "traffic over time",
    ],
    edenExamples: ['Try "show site visits over time" or "show device engagement"'],
    edenPlaceholder: 'Ask EDEN — "compare users and sessions"',
  },
  meta: {
    platformKey: "meta",
    audiencePlatformKey: "meta",
    audienceSupport: "platform_specific",
    routeKey: "/meta-analytics",
    pageTitle: "Meta Analytics",
    heroLabel: "Meta",
    heroTag: "Analytics",
    accent: "#0064e0",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpm", "cpc", "engagements", "conversions"],
    chartMetricKeys: ["spend", "impressions", "clicks", "engagements", "conversions"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "Meta Report",
    candidateFields: ["metaLink", "meta", "metaURL", "meta_report", "meta_analytics"],
    genericScan: true,
    genericChannelKey: "meta",
    pageCapabilities: [
      "Meta performance",
      "paid social analysis",
      "cross-publisher delivery review",
    ],
    edenExamples: ['Try "show Meta spend" or "how does Instagram compare?"'],
    edenPlaceholder: 'Ask EDEN — "show combined Meta delivery"',
  },
  tiktok: {
    platformKey: "tiktok",
    audiencePlatformKey: "tiktok",
    audienceSupport: "platform_specific",
    routeKey: "/tiktok-analytics",
    pageTitle: "TikTok Analytics",
    heroLabel: "TikTok",
    heroTag: "Analytics",
    accent: "#d8409f",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "videoViews", "videoCompletions", "vcr", "engagements"],
    chartMetricKeys: ["spend", "impressions", "clicks", "videoViews", "videoCompletions", "engagements"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "TikTok Report",
    candidateFields: ["tiktokLink", "tiktok", "tikTok", "tikTokLink", "tiktokURL", "tiktok_report"],
    genericScan: true,
    genericChannelKey: "tiktok",
    pageCapabilities: ["TikTok performance", "paid social analysis", "creative and delivery review"],
    edenExamples: ['Try "show video views" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show completed views for TikTok"',
  },
  google_search: {
    platformKey: "google_search",
    audiencePlatformKey: "google_search",
    audienceSupport: "platform_specific",
    routeKey: "/google-analytics",
    pageTitle: "Google Search Analytics",
    /*
     * GOOGLE + [SEARCH], built the same way as its GA4 sibling below: wordmark
     * names the company, capsule names the product. The capsule used to say
     * "Analytics" — the generic word every other page's capsule says — which
     * on a PAID SEARCH page was both uninformative and confusable with the
     * measurement page next door.
     *
     * `heroLabel` stays "Google Search" because that is what the Creative
     * Performance subtitle says out loud: "Which Google Search creatives
     * produced this platform's delivery". The wordmark alone would name the
     * company there, not the platform.
     *
     * The route stays /google-analytics and the key stays `google_search` —
     * both name the real, historically-conflated integration.
     */
    heroLabel: "Google Search",
    heroDisplayLabel: "Google",
    heroTag: "Search",
    accent: "#d96540",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpc", "conversions", "cpa", "cpm"],
    chartMetricKeys: ["spend", "impressions", "clicks", "conversions"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "Google Search Report",
    candidateFields: ["googleSearchLink", "googleSearch", "google_search", "googleSearchURL", "google_report"],
    genericScan: true,
    genericChannelKey: "google",
    pageCapabilities: ["search performance", "campaign efficiency review", "paid search reporting"],
    edenExamples: ['Try "show conversions" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show search conversions"',
  },
  youtube: {
    platformKey: "youtube",
    audiencePlatformKey: "youtube",
    audienceSupport: "platform_specific",
    routeKey: "/youtube-analytics",
    pageTitle: "YouTube Analytics",
    heroLabel: "YouTube",
    heroTag: "Analytics",
    accent: "#1e97cc",
    focusedKpiProfile: TRUEVIEW_DELIVERY_KPI_PROFILE,
    /*
     * YouTube keeps its CTR card: this is the platform's OWN page, and its
     * clicks are real reported evidence over its own denominator — a
     * single-source rate, not a blend. YouTube is still excluded from every
     * CROSS-platform click rate by the governed metricBasis engine.
     */
    commandMetricKeys: [
      "spend",
      "impressions",
      "clicks",
      "ctr",
      "trueViewViews",
      "cpqv",
      "youtubeVideoCompletions",
      "cpcv",
    ],
    // Only the dedicated analytics page consumes these additions. Shared
    // focused strips and public reports retain their existing eight cards.
    analyticsOnlyMetricKeys: [
      "youtubePlayed25Rate",
      "youtubePlayed50Rate",
      "youtubePlayed75Rate",
      "youtubePlayed100Rate",
    ],
    chartMetricKeys: ["spend", "impressions", "videoViews", "videoStarts", "videoCompletions", "clicks"],
    pairedEfficiencyKey: "vvr",
    /*
     * YouTube creatives are reported from the retained rows of the Google Ads
     * report worksheets (the same hourly read that feeds YouTube delivery),
     * resolved to Cphere assets through the verified identity mapping
     * registry. The backend adapter declares the columns; this key only opts
     * the page into the governed Creative Performance section.
     */
    creativePerformancePlatformKey: "youtube",
    reportSourceLabel: "YouTube Report",
    candidateFields: ["youtubeLink", "youtube", "YouTube", "yt", "ytLink", "youtubeURL", "youtube_report"],
    genericScan: true,
    genericChannelKey: "youtube",
    pageCapabilities: ["YouTube performance", "video delivery review", "creative and audience analysis"],
    edenExamples: ['Try "show 100% video completions" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show video view rate"',
  },
  x: {
    platformKey: "x",
    audiencePlatformKey: "x",
    audienceSupport: "platform_specific",
    routeKey: "/x-analytics",
    pageTitle: "X Analytics",
    heroLabel: "X",
    heroTag: "Analytics",
    accent: "#8d97ad",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpm", "engagements", "videoViews", "cpc"],
    chartMetricKeys: ["spend", "impressions", "clicks", "engagements", "videoViews"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "X Report",
    candidateFields: ["xLink", "x", "twitter", "Twitter", "twitterLink", "twitterURL"],
    pageCapabilities: ["X performance", "conversation and delivery review", "paid social analysis"],
    edenExamples: ['Try "show engagements" or "show the audience synthesis"'],
    edenPlaceholder: 'Ask EDEN — "show engagements for X"',
  },
  reddit: {
    platformKey: "reddit",
    audienceSupport: "shared_only",
    routeKey: "/reddit-analytics",
    pageTitle: "Reddit Analytics",
    heroLabel: "Reddit",
    heroTag: "Analytics",
    accent: "#e05a2b",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpm", "engagements"],
    chartMetricKeys: ["spend", "impressions", "clicks", "engagements"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "Reddit Report",
    candidateFields: [
      "redditLink",
      "reddit",
      "redditURL",
      "reddit_url",
      "reddit_report",
      "redditReport",
      "reddit_analytics",
      "redditAnalytics",
    ],
    pageCapabilities: ["Reddit performance", "community and paid social reporting", "delivery and engagement review"],
    edenExamples: ['Try "show clicks" or "show the audience synthesis"'],
    edenPlaceholder: 'Ask EDEN — "show Reddit engagement"',
  },
  amazon: {
    platformKey: "amazon",
    audienceSupport: "shared_only",
    routeKey: "/amazon-analytics",
    pageTitle: "Amazon Analytics",
    heroLabel: "Amazon",
    heroTag: "Analytics",
    accent: "#b5820f",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpc", "conversions", "cpa", "cpm"],
    chartMetricKeys: ["spend", "impressions", "clicks", "conversions"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "Amazon Report",
    candidateFields: [
      "amazon",
      "amazonLink",
      "amazonURL",
      "amazon_url",
      "amazon_report",
      "amazonReport",
      "amazon_analytics",
      "amazonAnalytics",
      "amazonAnalyticsLink",
    ],
    // Amazon conversions are not totalled (Amazon attributes them under
    // windows that differ by ad product), so this copy no longer suggests
    // them. The conversions and CPA metric keys above stay: imported Amazon
    // delivery reads "—" there, and other Amazon sources can still fill them.
    // The Eden prompt asks only what this page answers itself, the freshness
    // of its data: Eden's /ask cannot yet scope a spend or click question to
    // Amazon.
    pageCapabilities: ["Amazon performance", "retail media reporting", "campaign delivery and cost review"],
    edenExamples: ['Try "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "how fresh is this data?"',
  },
  linkedin: {
    platformKey: "linkedin",
    audienceSupport: "shared_only",
    routeKey: "/linkedin-analytics",
    pageTitle: "LinkedIn Ads Analytics",
    heroLabel: "LinkedIn Ads",
    heroTag: "Analytics",
    accent: "#3286c9",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpc", "leads", "conversions", "cpm"],
    chartMetricKeys: ["spend", "impressions", "clicks", "leads", "conversions"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "LinkedIn Ads Report",
    candidateFields: [
      "linkedin",
      "linkedIn",
      "linkedinAds",
      "linkedinURL",
      "linkedin_url",
      "linkedin_report",
      "linkedinReport",
      "linkedin_analytics",
      "linkedinAnalytics",
      "linkedinAnalyticsLink",
      "linkedInAnalyticsLink",
    ],
    genericScan: true,
    genericChannelKey: "linkedin",
    pageCapabilities: [
      "LinkedIn Ads reporting",
      "spend, impressions, clicks, CTR, and CPC",
      "leads, conversions, campaign groups, campaigns, and creatives",
    ],
    edenExamples: ['Try "show leads" or "show the audience synthesis"'],
    edenPlaceholder: 'Ask EDEN — "show LinkedIn leads"',
  },
  shopify: {
    platformKey: "shopify",
    audienceSupport: "shared_only",
    routeKey: "/shopify-analytics",
    pageTitle: "Shopify Analytics",
    heroLabel: "Shopify",
    heroTag: "Analytics",
    accent: "#5aa763",
    commandMetricKeys: ["revenue", "orders", "aov", "customers"],
    chartMetricKeys: ["revenue", "orders", "customers"],
    pairedEfficiencyKey: "aov",
    reportSourceLabel: "Shopify Report",
    candidateFields: [
      "shopify",
      "shopifyLink",
      "shopifyURL",
      "shopify_url",
      "shopify_report",
      "shopifyReport",
      "shopify_analytics",
      "shopifyAnalytics",
      "shopifyAnalyticsLink",
      "shopifyCommerceReport",
    ],
    genericScan: true,
    genericChannelKey: "shopify",
    pageCapabilities: [
      "read-only commerce reporting",
      "revenue, orders, and AOV",
      "customers, discounts, inventory, and product signals",
    ],
    edenExamples: ['Try "show orders" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show Shopify revenue"',
  },
  truthsocial: {
    platformKey: "truthsocial",
    audienceSupport: "shared_only",
    routeKey: "/truthsocial-analytics",
    pageTitle: "Truth Social Analytics",
    heroLabel: "Truth Social",
    heroTag: "Analytics",
    accent: "#7d64d9",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "engagements", "videoViews"],
    chartMetricKeys: ["spend", "impressions", "clicks", "engagements", "videoViews"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "Truth Social Report",
    candidateFields: [
      "truthSocialLink",
      "truthSocial",
      "truthsocial",
      "truthsocialURL",
      "truthsocial_report",
    ],
    pageCapabilities: ["Truth Social performance", "social reporting", "content and delivery review"],
    edenExamples: ['Try "show engagements" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show Truth Social engagement"',
  },
  rumble: {
    platformKey: "rumble",
    audienceSupport: "shared_only",
    routeKey: "/rumble-analytics",
    pageTitle: "Rumble Analytics",
    heroLabel: "Rumble",
    heroTag: "Analytics",
    accent: "#74b043",
    commandMetricKeys: ["spend", "impressions", "videoViews", "vcr", "videoCompletions", "clicks", "ctr", "cpm"],
    chartMetricKeys: ["spend", "impressions", "videoViews", "videoStarts", "videoCompletions", "clicks"],
    pairedEfficiencyKey: "vcr",
    reportSourceLabel: "Rumble Report",
    candidateFields: ["rumbleLink", "rumble", "rumbleURL", "rumble_report"],
    pageCapabilities: ["Rumble performance", "video delivery reporting", "content and audience review"],
    edenExamples: ['Try "show video views" or "show the audience synthesis"'],
    edenPlaceholder: 'Ask EDEN — "show Rumble video views"',
  },
  chatgpt_search: {
    platformKey: "chatgpt_search",
    audienceSupport: "shared_only",
    routeKey: "/chatgpt-search-analytics",
    pageTitle: "ChatGPT Search Analytics",
    /*
     * CHATGPT + [SEARCH], built the same way as GOOGLE + [SEARCH] above: the
     * wordmark names the platform, the capsule names the product. It used to
     * be the whole name beside an "Analytics" capsule, which broke onto two
     * lines on a phone.
     *
     * `heroLabel` stays "ChatGPT Search" because it is what the page says out
     * loud (legends, subtitles, the narrative). The route and the
     * `chatgpt_search` key are unchanged.
     */
    heroLabel: "ChatGPT Search",
    heroDisplayLabel: "ChatGPT",
    heroTag: "Search",
    accent: "#4aa6a0",
    commandMetricKeys: ["spend", "impressions", "clicks", "ctr", "cpc", "conversions", "cpa", "cpm"],
    chartMetricKeys: ["spend", "impressions", "clicks", "conversions"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "ChatGPT Search Report",
    candidateFields: [
      "chatgptSearchLink",
      "chatgptSearch",
      "chatgpt_search",
      "chatgptSearchURL",
      "chatgpt_search_report",
    ],
    pageCapabilities: ["AI search intent performance", "AI search inventory review", "StackAdapt-routed reporting"],
    edenExamples: ['Try "show clicks" or "how fresh is this data?"'],
    edenPlaceholder: 'Ask EDEN — "show ChatGPT Search clicks"',
  },
  metricool: {
    platformKey: "metricool",
    audienceSupport: "shared_only",
    /*
     * Canonical route correction: the live App route is /community-analytics.
     * The legacy wrapper declared routeKey="/metricool-analytics", which does
     * not exist in App.js — the profile binds to the real route.
     */
    routeKey: "/community-analytics",
    pageTitle: "Metricool Analytics",
    heroLabel: "Metricool",
    heroTag: "Analytics",
    accent: "#38a1b8",
    commandMetricKeys: ["impressions", "engagements", "clicks", "ctr", "videoViews", "videoCompletions"],
    chartMetricKeys: ["impressions", "engagements", "clicks", "videoViews"],
    pairedEfficiencyKey: "ctr",
    reportSourceLabel: "Metricool Report",
    candidateFields: [
      "metricoolUrl",
      "metricool_url",
      "metricoolLink",
      "metricool_link",
      "metricool",
      "metricoolURL",
      "metricool_report",
      "metricoolLookerUrl",
      "metricool_looker_url",
      "metricoolLookerURL",
      "metricool_dashboard",
    ],
    genericScan: true,
    genericChannelKey: "metricool",
    pageCapabilities: ["cross-platform reporting", "social analytics review", "dashboard visibility"],
    edenExamples: ['Try "show engagements" or "show the audience synthesis"'],
    edenPlaceholder: 'Ask EDEN — "show community engagement"',
  },
};

export function resolvePlatformAnalyticsProfile(platformKey) {
  return PLATFORM_ANALYTICS_PROFILES[String(platformKey || "").toLowerCase()] || null;
}

export const PAID_MEDIA_SOURCE_TYPE = "paid_media";
export const MEASUREMENT_SOURCE_TYPE = "measurement";

/*
 * Absent means paid media. Every profile that predates the measurement seam
 * therefore keeps its exact behavior without being edited, which is what makes
 * this safe to introduce under a fleet of live pages.
 */
export function resolveProfileSourceType(profile) {
  return profile?.sourceType === MEASUREMENT_SOURCE_TYPE
    ? MEASUREMENT_SOURCE_TYPE
    : PAID_MEDIA_SOURCE_TYPE;
}

/*
 * Media-cost vocabulary is a paid-media entitlement, not a global one. A
 * measurement page reports no spend, so a basis control there would assert a
 * media cost that does not exist — in Quantum as much as in Cortex.
 */
export function profileSupportsSpendBasis(profile) {
  return resolveProfileSourceType(profile) === PAID_MEDIA_SOURCE_TYPE;
}

/*
 * The chart metrics that describe MEDIA DELIVERY — what a platform served and
 * what it cost. Revenue, orders and customers are commerce outcomes reported by
 * a store, not delivery, and a page whose chart plots only those has no media
 * trend for a site-visit series to sit against.
 */
const MEDIA_DELIVERY_CHART_METRIC_KEYS = new Set([
  "spend",
  "impressions",
  "clicks",
  "videoViews",
  "videoStarts",
  "videoCompletions",
  "engagements",
  "conversions",
]);

/*
 * Whether this page's Delivery chart may carry the observed Site Visits · GA4
 * overlay.
 *
 * The overlay is a CONTEXTUAL reading: observed site activity during the same
 * dates as this platform's delivery. That reading needs two things, and both
 * are properties of the page rather than of the data that happens to arrive:
 *
 *  - Media delivery to sit against. A commerce-only page (Shopify: revenue,
 *    orders, customers) plots outcomes a store reported, not media a platform
 *    served; laying sessions over it would pair two site-side measurements and
 *    imply one drove the other.
 *  - Something other than site measurement on the left axis. GA4's own page IS
 *    the site-visit surface — overlaying observed sessions on observed sessions
 *    draws one fact twice. That page keeps its native measurement presentation.
 *
 * Nothing here asks whether GA4 has data. Whether the series EXISTS is decided
 * by the evidence; whether it BELONGS is decided here.
 */
export function profileSupportsSiteVisitsOverlay(profile) {
  if (!profile) return false;
  if (resolveProfileSourceType(profile) !== PAID_MEDIA_SOURCE_TYPE) return false;
  return (profile.chartMetricKeys || []).some((key) =>
    MEDIA_DELIVERY_CHART_METRIC_KEYS.has(key)
  );
}
